const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

class ApiService {
  constructor() {
    this.tokenExpiryCallback = null;
  }

  setTokenExpiryCallback(callback) {
    this.tokenExpiryCallback = callback;
  }

  getAuthHeaders() {
    const token = localStorage.getItem("auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async makeRequest(url, options = {}) {
    const config = {
      headers: {
        "Content-Type": "application/json",
        ...this.getAuthHeaders(),
        ...options.headers,
      },
      ...options,
    };
    try {
      const response = await fetch(`${API_BASE_URL}${url}`, config);

      // Handle token expiry - but NOT for auth endpoints (login, reset, etc.)
      const isAuthEndpoint = url.startsWith("/auth/");
      if (
        (response.status === 401 || response.status === 403) &&
        !isAuthEndpoint &&
        this.tokenExpiryCallback
      ) {
        this.tokenExpiryCallback();
        throw new Error("Session expired");
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        return await response.json();
      }

      return await response.text();
    } catch (error) {
      console.error(`API Error (${url}):`, error);
      throw error;
    }
  }

  // Authentication
  async login(credentials) {
    return this.makeRequest("/auth/login", {
      method: "POST",
      body: JSON.stringify(credentials),
    });
  }

  async switchClub(clubId) {
    return this.makeRequest("/auth/switch-club", {
      method: "POST",
      body: JSON.stringify({ clubId }),
    });
  }

  async loginWithCode(email, accessCode) {
    return this.makeRequest("/auth/login-with-code", {
      method: "POST",
      body: JSON.stringify({ email, accessCode }),
    });
  }

  async verifyCode(accessCode) {
    return this.makeRequest("/auth/verify-code", {
      method: "POST",
      body: JSON.stringify({ accessCode }),
    });
  }

  async changePassword(passwordData) {
    return this.makeRequest("/auth/change-password", {
      method: "POST",
      body: JSON.stringify(passwordData),
    });
  }

  async resetPassword(email) {
    return this.makeRequest("/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  }

  async forceChangePassword(passwordData) {
    return this.makeRequest("/auth/force-change-password", {
      method: "POST",
      body: JSON.stringify(passwordData),
    });
  }

  // Clubs
  async getClubStats(clubId) {
    return this.makeRequest(`/clubs/${clubId}/stats`);
  }

  async getClubStaff(clubId) {
    return this.makeRequest(`/clubs/${clubId}/staff`);
  }

  async inviteStaff(clubId, inviteData) {
    return this.makeRequest(`/clubs/${clubId}/staff/invite`, {
      method: "POST",
      body: JSON.stringify(inviteData),
    });
  }

  async removeStaff(clubId, userId) {
    return this.makeRequest(`/clubs/${clubId}/staff/${userId}`, {
      method: "DELETE",
    });
  }

  async updateStaff(clubId, userId, updates) {
    return this.makeRequest(`/clubs/${clubId}/staff/${userId}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  }

  async createEvaluatorByEmail(evaluatorData) {
    return this.makeRequest("/staff/create-evaluator", {
      method: "POST",
      body: JSON.stringify(evaluatorData),
    });
  }

  async getClubSettings(clubId) {
    return this.makeRequest(`/clubs/${clubId}/settings`);
  }

  async updateClubSettings(clubId, settings) {
    return this.makeRequest(`/clubs/${clubId}/settings`, {
      method: "PUT",
      body: JSON.stringify(settings),
    });
  }

  async updateClubBrandingSettings(clubId, settings) {
    return this.makeRequest(`/clubs/${clubId}/settings`, {
      method: "PATCH",
      body: JSON.stringify(settings),
    });
  }

  async getClubInfo(clubId) {
    return this.makeRequest(`/clubs/${clubId}/info`);
  }

  async setClubInfo(clubId, info) {
    return this.makeRequest(`/clubs/${clubId}/info`, {
      method: "PUT",
      body: JSON.stringify(info),
    });
  }

  async uploadClubLogo(clubId, formData) {
    // Use fetch directly — makeRequest sets Content-Type: application/json which
    // breaks multipart/form-data. The browser sets the correct boundary automatically.
    const response = await fetch(`${API_BASE_URL}/clubs/${clubId}/logo`, {
      method: "POST",
      headers: this.getAuthHeaders(),
      body: formData,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  async deleteClubLogo(clubId) {
    return this.makeRequest(`/clubs/${clubId}/logo`, {
      method: "DELETE",
    });
  }

  // Add user to additional clubs
  async addUserToClub(userId, sessionId, role) {
    return this.makeRequest("/auth/add-user-to-club", {
      method: "POST",
      body: JSON.stringify({ userId, sessionId, role }),
    });
  }

  // Players
  async getPlayers(clubId) {
    return this.makeRequest(`/clubs/${clubId}/players`);
  }

  async importPlayers(clubId, playersData) {
    return this.makeRequest(`/clubs/${clubId}/players/import`, {
      method: "POST",
      body: JSON.stringify(playersData),
    });
  }

  async deletePlayer(playerId) {
    return this.makeRequest(`/players/${playerId}`, {
      method: "DELETE",
    });
  }

  // Evaluations
  async getEvaluations(clubId) {
    return this.makeRequest(`/evaluations/clubs/${clubId}`);
  }

  async updateEvaluation(evaluationId, evaluationData) {
    return this.makeRequest(`/evaluations/${evaluationId}`, {
      method: "PATCH",
      body: JSON.stringify(evaluationData),
    });
  }

  async deleteEvaluation(evaluationId) {
    return this.makeRequest(`/evaluations/${evaluationId}`, {
      method: "DELETE",
    });
  }

  // Evaluation Sessions
  async getEvaluationSessions(evaluationId) {
    return this.makeRequest(`/evaluations/${evaluationId}/sessions`);
  }

  async createEvaluationSession(evaluationId, sessionData) {
    return this.makeRequest(`/evaluations/${evaluationId}/sessions`, {
      method: "POST",
      body: JSON.stringify(sessionData),
    });
  }

  async updateEvaluationSession(sessionId, sessionData) {
    return this.makeRequest(`/sessions/${sessionId}`, {
      method: "PUT",
      body: JSON.stringify(sessionData),
    });
  }

  async deleteEvaluationSession(sessionId) {
    return this.makeRequest(`/sessions/${sessionId}`, {
      method: "DELETE",
    });
  }

  async getSessionDetails(sessionId) {
    return this.makeRequest(`/sessions/${sessionId}`);
  }

  // Session Check-ins
  async getSessionCheckins(sessionId) {
    return this.makeRequest(`/sessions/${sessionId}/checkins`);
  }

  async checkinPlayer(sessionId, playerId, evaluationNumber) {
    return this.makeRequest(`/sessions/${sessionId}/checkins`, {
      method: "POST",
      body: JSON.stringify({ playerId, evaluationNumber }),
    });
  }

  async updateCheckin(checkinId, checkinData) {
    return this.makeRequest(`/checkins/${checkinId}`, {
      method: "PUT",
      body: JSON.stringify(checkinData),
    });
  }

  async removeCheckin(checkinId) {
    return this.makeRequest(`/checkins/${checkinId}`, {
      method: "DELETE",
    });
  }

  // Session Staff
  async getSessionStaff(sessionId) {
    return this.makeRequest(`/sessions/${sessionId}/staff`);
  }

  async assignStaffToSession(sessionId, staffData) {
    return this.makeRequest(`/sessions/${sessionId}/staff`, {
      method: "POST",
      body: JSON.stringify(staffData),
    });
  }

  async removeStaffFromSession(sessionId, userId) {
    return this.makeRequest(`/sessions/${sessionId}/staff/${userId}`, {
      method: "DELETE",
    });
  }

  async getClubSkills(clubId) {
    return this.makeRequest(`/clubs/${clubId}/skills`);
  }

  // Evaluation Groups
  async getEvaluationGroups(clubId) {
    return this.makeRequest(`/groups/${clubId}`);
  }

  async createEvaluationGroup(clubId, groupData) {
    return this.makeRequest(`/groups/${clubId}`, {
      method: "POST",
      body: JSON.stringify(groupData),
    });
  }

  async updateEvaluationGroup(clubId, groupId, groupData) {
    return this.makeRequest(`/groups/${clubId}/${groupId}`, {
      method: "PUT",
      body: JSON.stringify(groupData),
    });
  }

  async deleteEvaluationGroup(clubId, groupId) {
    return this.makeRequest(`/groups/${clubId}/${groupId}`, {
      method: "DELETE",
    });
  }

  // Evaluation Results
  async getEvaluationResults(evaluationId) {
    return this.makeRequest(`/evaluations/${evaluationId}/results`);
  }

  async getSkillBasedResults(evaluationId) {
    return this.makeRequest(`/evaluations/${evaluationId}/skill-results`);
  }

  async getEvaluationCalibration(evaluationId) {
    return this.makeRequest(`/evaluations/${evaluationId}/calibration`);
  }

  async normalizeEvaluationScores(evaluationId) {
    return this.makeRequest(`/evaluations/${evaluationId}/normalize-scores`, {
      method: "POST",
    });
  }

  async getSkillCategoriesForEvaluation(evaluationId) {
    return this.makeRequest(`/evaluations/${evaluationId}/skill-categories`);
  }

  async getSkills(evaluationId) {
    return this.makeRequest(`/evaluations/${evaluationId}/skills`);
  }

  async getParentReport(evaluationId, playerId) {
    return this.makeRequest(
      `/evaluations/${evaluationId}/players/${playerId}/parent-report`,
    );
  }

  async sendParentReports(evaluationId, playerIds = null) {
    return this.makeRequest(
      `/evaluations/${evaluationId}/send-parent-reports`,
      {
        method: "POST",
        body: JSON.stringify(playerIds ? { playerIds } : {}),
      },
    );
  }

  async getTeamAssignments(evaluationId) {
    return this.makeRequest(`/evaluations/${evaluationId}/team-assignments`);
  }

  async updateTeamAssignments(evaluationId, assignments) {
    return this.makeRequest(`/evaluations/${evaluationId}/team-assignments`, {
      method: "PATCH",
      body: JSON.stringify({ assignments }),
    });
  }

  async saveEvaluationResults(evaluationId, resultsData) {
    return this.makeRequest(`/evaluations/${evaluationId}/results`, {
      method: "POST",
      body: JSON.stringify(resultsData),
    });
  }

  async getEvaluationData(evaluationId) {
    return this.makeRequest(`/evaluations/${evaluationId}/data`);
  }

  // File uploads
  async uploadFile(file, path = "") {
    const formData = new FormData();
    formData.append("file", file);
    if (path) formData.append("path", path);

    return this.makeRequest("/upload", {
      method: "POST",
      headers: {
        // Don't set Content-Type for FormData, let browser set it
        ...this.getAuthHeaders(),
      },
      body: formData,
    });
  }

  // Export/Import
  async exportEvaluationResults(evaluationId, format = "csv") {
    const response = await fetch(
      `${API_BASE_URL}/evaluations/${evaluationId}/export?format=${format}`,
      {
        headers: this.getAuthHeaders(),
      },
    );

    if (!response.ok) {
      throw new Error("Export failed");
    }

    return response.blob();
  }

  async importPlayersFromCSV(clubId, file) {
    const formData = new FormData();
    formData.append("file", file);

    return this.makeRequest(`/clubs/${clubId}/players/import-csv`, {
      method: "POST",
      headers: {
        ...this.getAuthHeaders(),
      },
      body: formData,
    });
  }

  // User Profile
  async getUserProfile() {
    return this.makeRequest("/user/profile");
  }

  async updateUserProfile(profileData) {
    return this.makeRequest("/user/profile", {
      method: "PUT",
      body: JSON.stringify(profileData),
    });
  }

  // User's clubs
  async getUserClubs() {
    return this.makeRequest("/user/clubs");
  }

  async updatePrimaryClub(clubId) {
    return this.makeRequest("/user/primary-club", {
      method: "PUT",
      body: JSON.stringify({ clubId }),
    });
  }

  // Health check
  async healthCheck() {
    return this.makeRequest("/health");
  }

  // Club methods
  async createClub(clubData) {
    return this.makeRequest("/clubs", {
      method: "POST",
      body: JSON.stringify(clubData),
    });
  }

  async updateClub(clubId, updates) {
    return this.makeRequest(`/clubs/${clubId}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  }

  async getAllClubs() {
    return this.makeRequest("/clubs");
  }

  async getClub(clubId) {
    return this.makeRequest(`/clubs/${clubId}`);
  }

  async getClubSubscriptionLimits(clubId) {
    return this.makeRequest(`/clubs/${clubId}/subscription`);
  }

  async assignSubscriptionToClub(clubId, subscriptionData) {
    return this.makeRequest(`/clubs/${clubId}/subscription`, {
      method: "POST",
      body: JSON.stringify(subscriptionData),
    });
  }

  // Evaluation methods
  async createEvaluation(evalData) {
    return this.makeRequest("/evaluations", {
      method: "POST",
      body: JSON.stringify(evalData),
    });
  }

  async getClubEvaluations(clubId) {
    return this.makeRequest(`/evaluations/clubs/${clubId}`);
  }

  async getEvaluation(evalId) {
    return this.makeRequest(`/evaluations/${evalId}/data`);
  }

  async createSession(sessionData) {
    return this.makeRequest(
      `/evaluations/${sessionData.evaluation_id}/sessions`,
      {
        method: "POST",
        body: JSON.stringify(sessionData),
      },
    );
  }

  async updateSession(sessionId, updates) {
    return this.makeRequest(
      `/evaluations/${updates.evaluation_id}/sessions/${sessionId}`,
      {
        method: "PATCH",
        body: JSON.stringify(updates),
      },
    );
  }

  async getSession(sessionId) {
    return this.makeRequest(`/sessions/${sessionId}`);
  }

  async deleteSession(sessionId) {
    return this.makeRequest(`/sessions/${sessionId}`, {
      method: "DELETE",
    });
  }

  // Check-in methods
  async getSessionCheckIns(sessionId) {
    return this.makeRequest(`/sessions/${sessionId}/checkins`);
  }

  async createCheckIn(checkInData) {
    return this.makeRequest(`/sessions/${checkInData.session_id}/checkins`, {
      method: "POST",
      body: JSON.stringify(checkInData),
    });
  }

  async removeCheckIn(sessionId, playerId) {
    return this.makeRequest(`/sessions/${sessionId}/checkins/${playerId}`, {
      method: "DELETE",
    });
  }

  async getEvaluationPlayers(evalId) {
    return this.makeRequest(`/evaluations/${evalId}/players`);
  }

  // Player methods
  async getClubPlayers(clubId) {
    return this.makeRequest(`/clubs/${clubId}/players`);
  }

  async createPlayer(playerData) {
    return this.makeRequest("/players", {
      method: "POST",
      body: JSON.stringify(playerData),
    });
  }

  async updatePlayer(playerId, updates) {
    return this.makeRequest(`/players/${playerId}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  }

  async bulkCreatePlayers(playersData) {
    return this.makeRequest("/players/bulk", {
      method: "POST",
      body: JSON.stringify({ players: playersData }),
    });
  }

  async unassignStaffFromSession(sessionId, assignmentId) {
    return this.makeRequest(`/sessions/${sessionId}/staff/${assignmentId}`, {
      method: "DELETE",
    });
  }

  async getClubSkillCategories(clubId) {
    return this.makeRequest(`/clubs/${clubId}/skill-categories`);
  }

  async getClubRatingScales(clubId) {
    return this.makeRequest(`/clubs/${clubId}/rating-scales`);
  }

  async createSkillCategory(categoryData) {
    return this.makeRequest(`/skill-categories`, {
      method: "POST",
      body: JSON.stringify(categoryData),
    });
  }

  async updateSkillCategory(categoryId, updates) {
    return this.makeRequest(`/skill-categories/${categoryId}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  }

  async deleteSkillCategory(categoryId) {
    return this.makeRequest(`/skill-categories/${categoryId}`, {
      method: "DELETE",
    });
  }

  async createSkill(skillData) {
    return this.makeRequest(`/skills`, {
      method: "POST",
      body: JSON.stringify(skillData),
    });
  }

  async updateSkill(skillId, updates) {
    return this.makeRequest(`/skills/${skillId}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  }

  async deleteSkill(skillId) {
    return this.makeRequest(`/skills/${skillId}`, {
      method: "DELETE",
    });
  }

  async addSessionSkillCategories(sessionId, skillsData) {
    return this.makeRequest(`/sessions/${sessionId}/skills`, {
      method: "POST",
      body: JSON.stringify(skillsData),
    });
  }

  async getBillingOverview() {
    return this.makeRequest("/billing/overview");
  }

  async getAllClubsWithUsage() {
    return this.makeRequest("/billing/clubs");
  }

  async getAllInvoices(filters = {}) {
    const params = new URLSearchParams();

    if (filters.status) params.append("status", filters.status);
    if (filters.year) params.append("year", filters.year);
    if (filters.page) params.append("page", filters.page);
    if (filters.limit) params.append("limit", filters.limit);

    const queryString = params.toString();
    const url = queryString
      ? `/billing/invoices?${queryString}`
      : "/billing/invoices";

    return this.makeRequest(url);
  }

  async getSubscriptionPlans() {
    return this.makeRequest("/billing/subscription-plans");
  }

  async getPublicPlans() {
    return this.makeRequest("/billing/public-plans");
  }

  async getCurrentMonthUsage() {
    return this.makeRequest("/billing/current-month-usage");
  }

  async generateAllInvoices() {
    return this.makeRequest("/billing/generate-invoices", {
      method: "POST",
    });
  }

  async recalculateAllUsage() {
    return this.makeRequest("/billing/recalculate-usage", {
      method: "POST",
    });
  }

  async markInvoiceAsPaid(invoiceId) {
    return this.makeRequest(`/billing/invoices/${invoiceId}/mark-paid`, {
      method: "POST",
    });
  }

  async sendInvoiceReminder(invoiceId) {
    return this.makeRequest(`/billing/invoices/${invoiceId}/send-reminder`, {
      method: "POST",
    });
  }

  async downloadInvoice(invoiceId) {
    // Special handling for PDF downloads - we need to get the blob directly
    const response = await fetch(
      `${API_BASE_URL}/billing/invoices/${invoiceId}/download`,
      {
        method: "GET",
        headers: {
          ...this.getAuthHeaders(),
        },
      },
    );

    // Handle token expiry
    if (response.status === 401 && this.tokenExpiryCallback) {
      this.tokenExpiryCallback();
      throw new Error("Session expired");
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    // Return the blob directly for PDF files
    return await response.blob();
  }

  async getInvoice(invoiceId) {
    return this.makeRequest(`/billing/invoices/${invoiceId}`);
  }

  async createSubscriptionPlan(planData) {
    return this.makeRequest("/billing/subscription-plans", {
      method: "POST",
      body: JSON.stringify(planData),
    });
  }

  async updateSubscriptionPlan(planId, updates) {
    return this.makeRequest(`/billing/subscription-plans/${planId}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  }

  async deleteSubscriptionPlan(planId) {
    return this.makeRequest(`/billing/subscription-plans/${planId}`, {
      method: "DELETE",
    });
  }

  // FAQ Methods
  async getPublicFaqs() {
    return this.makeRequest("/billing/public-faqs");
  }

  async getFaqs() {
    return this.makeRequest("/billing/faqs");
  }

  async createFaq(data) {
    return this.makeRequest("/billing/faqs", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateFaq(id, updates) {
    return this.makeRequest(`/billing/faqs/${id}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  }

  async deleteFaq(id) {
    return this.makeRequest(`/billing/faqs/${id}`, {
      method: "DELETE",
    });
  }

  // Contact Form
  async submitContact(data) {
    return this.makeRequest("/contact", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Session Assignment Methods (for workflow 2)
  async getSessionAssignments(sessionId) {
    return this.makeRequest(`/sessions/${sessionId}/assignments`);
  }

  async assignPlayersToSession(sessionId, playerIds) {
    return this.makeRequest(`/sessions/${sessionId}/assignments`, {
      method: "POST",
      body: JSON.stringify({ playerIds }),
    });
  }

  async copySessionAssignments(sessionId, sourceSessionId) {
    return this.makeRequest(`/sessions/${sessionId}/assignments/copy`, {
      method: "POST",
      body: JSON.stringify({ sourceSessionId }),
    });
  }

  async removePlayerAssignment(sessionId, playerId) {
    return this.makeRequest(`/sessions/${sessionId}/assignments/${playerId}`, {
      method: "DELETE",
    });
  }

  // Groups API methods
  async getGroupsByClub(clubId) {
    return this.makeRequest(`/groups/${clubId}`);
  }

  async createGroup(clubId, groupData) {
    return this.makeRequest(`/groups/${clubId}`, {
      method: "POST",
      body: JSON.stringify(groupData),
    });
  }

  async updateGroup(clubId, groupId, groupData) {
    return this.makeRequest(`/groups/${clubId}/${groupId}`, {
      method: "PUT",
      body: JSON.stringify(groupData),
    });
  }

  async deleteGroup(clubId, groupId) {
    return this.makeRequest(`/groups/${clubId}/${groupId}`, {
      method: "DELETE",
    });
  }

  // Session Groups API methods
  async getSessionGroups(sessionId) {
    return this.makeRequest(`/sessions/${sessionId}/groups`);
  }

  async saveSessionGroups(sessionId, groupsData) {
    return this.makeRequest(`/sessions/${sessionId}/groups`, {
      method: "POST",
      body: JSON.stringify(groupsData),
    });
  }

  // Player Photo API methods
  async uploadPlayerPhoto(playerId, photoFile) {
    const formData = new FormData();
    formData.append("photo", photoFile);

    return this.makeRequest(`/players/${playerId}/photo`, {
      method: "POST",
      headers: {
        // Don't set Content-Type for FormData, let browser set it
        ...this.getAuthHeaders(),
      },
      body: formData,
    });
  }

  async deletePlayerPhoto(playerId) {
    return this.makeRequest(`/players/${playerId}/photo`, {
      method: "DELETE",
    });
  }

  async updateRatingScale(ratingScaleId, updates) {
    return this.makeRequest(`/rating-scales/${ratingScaleId}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    });
  }

  async createRatingScale(scaleData) {
    return this.makeRequest(`/rating-scales`, {
      method: "POST",
      body: JSON.stringify(scaleData),
    });
  }
}

export const apiService = new ApiService();
